<template>
  <div class="auction-venue">
    <div v-if="loading" class="loading-container">
      <a-spin :spinning="loading" :tip="loading ? '加载中...' : ''" />
    </div>
    <div v-else>
      <!-- 面包屑导航 -->
      <div class="breadcrumb-wrapper">
        <a-breadcrumb>
          <a-breadcrumb-item>
            <router-link to="/business/venue">
              <a-icon type="home" />
              会场列表
            </router-link>
          </a-breadcrumb-item>
          <a-breadcrumb-item>{{ venueInfo.name }}</a-breadcrumb-item>
        </a-breadcrumb>
      </div>

      <!-- 需求竞拍 -->
      <template v-if="venueInfo.activity === 1">
        <div class="auction-container">
          <!-- 左侧：需求池 -->
          <RequirementPool :requirement-pool="requirementPool" :current-requirement-index="currentRequirementIndex" :activity-type="venueInfo.activity" />

          <!-- 右侧：根据activity类型显示不同的活动组件 -->
          <!-- 需求竞拍 -->
          <AuctionArena :venue-info="venueInfo" :initial-requirements="requirementPool" :initial-teams="teamList" :pending-records="pendingRecords" @change="loadVenueData" />
        </div>
      </template>

      <!-- 需求预约 -->
      <template v-else-if="venueInfo.activity === 2">
        <div class="reservation-arena">
          <div class="arena-title">
            <a-icon type="calendar" />
            预约大厅
            <div class="arena-subtitle">Reservation Hall</div>
          </div>
          <div class="coming-soon">
            <a-result status="info" title="预约功能即将上线" sub-title="敬请期待..." />
          </div>
        </div>
      </template>

      <!-- 排队选择 -->
      <template v-else-if="venueInfo.activity === 3">
        <QueueSelectionArenaNew :venue-info="venueInfo" :initial-requirements="requirementPool" :initial-teams="teamList" :pending-records="pendingRecords" @change="loadVenueData" />
      </template>

      <!-- 未知活动类型 -->
      <template v-else>
        <div v-if="loading === false" class="unknown-activity">
          <a-result status="warning" title="未知的活动类型" :sub-title="`activity: ${venueInfo.activity}`" />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { getVenueDetail } from '@/api/business/venue';

import AuctionArena from '@/pages/business/venue/components/AuctionArena';
import RequirementPool from '@/pages/business/venue/components/RequirementPool';
import QueueSelectionArenaNew from '@/pages/business/venue/components/QueueSelectionArenaNew';

export default {
  name: 'AuctionVenue',
  components: {
    RequirementPool,
    AuctionArena,
    QueueSelectionArenaNew
  },
  data() {
    return {
      // 会场基本信息
      venueInfo: {
        id: null,
        name: '',
        status: 0,
        activity: 0,
        start_time: '',
        description: '',
        creator: null,
        created_time: '',
        updated_time: ''
      },

      // 基础数据 - 只管理初始数据
      requirementPool: [],
      teamList: [],
      pendingRecords: [],
      // 当前状态 - 用于需求池显示
      currentRequirementIndex: 0,

      loading: true,

      // 轮询相关
      pollingTimer: null,
      pollingInterval: 2000 // 2秒
    };
  },
  computed: {
    // 判断是否需要轮询（状态为2时轮询）
    shouldPoll() {
      return this.venueInfo.status === 2;
    }
  },
  watch: {
    // 监听状态变化，决定是否开启/关闭轮询
    shouldPoll: {
      handler(newVal) {
        if (newVal) {
          this.startPolling();
        } else {
          this.stopPolling();
        }
      },
      immediate: false
    }
  },
  mounted() {
    this.loadInitialData();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async loadInitialData() {
      this.loading = true;
      try {
        await this.loadVenueData();
      } catch (error) {
        this.$message.error('加载数据失败');
      } finally {
        this.loading = false;
      }
    },

    async loadVenueData() {
      try {
        const venueId = parseInt(this.$route.params.id) || 2;
        const response = await getVenueDetail({ id: venueId });

        if (response.code === 2000 && response.data) {
          const data = response.data;
          const previousStatus = this.venueInfo.status;

          // 更新会场基本信息
          this.venueInfo = {
            id: data.id,
            name: data.name,
            status: data.status,
            activity: data.activity,
            start_time: data.start_time,
            description: data.description,
            creator: data.creator,
            records: data.records,
            created_time: data.created_time,
            updated_time: data.updated_time
          };
          this.pendingRecords = data.pendingRecords;

          // 处理需求数据
          this.processRequirements(data.products || []);

          // 处理参与者数据
          this.processParticipant(data.participants || []);

          // 首次加载后，如果状态为2，启动轮询
          if (previousStatus !== data.status && [1, 2].includes(data.status)) {
            this.startPolling();
          } else if (previousStatus !== data.status && ![1, 2].includes(data.status)) {
            this.stopPolling();
          }
        }
      } catch (error) {
        console.error('加载会场数据失败:', error);
      }
    },

    processRequirements(businessRequirements) {
      this.requirementPool = businessRequirements.map(req => ({
        ...req,
        id: req.id,
        title: req.source_name || `需求 ${req.source_id}`
      }));
    },

    processParticipant(participants) {
      this.teamList = participants.map(item => ({
        id: item.id,
        name: item.name,
        ownerId: item.owner_id,
        result: null,
        ready: item.ready
      }));
    },

    // ==================== 轮询控制 ====================
    startPolling() {
      if (this.pollingTimer) {
        return; // 已经在轮询中
      }

      console.log('开始轮询会场数据');
      this.pollingTimer = setInterval(() => {
        this.loadVenueData();
      }, this.pollingInterval);
    },

    stopPolling() {
      if (this.pollingTimer) {
        console.log('停止轮询会场数据');
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    }
  }
};
</script>

<style lang="less" scoped>
.auction-venue {
  margin: 0;

  // 面包屑导航样式
  .breadcrumb-wrapper {
    background: #ffffff;
    border-bottom: 1px solid #f0f0f0;
    padding: 12px 24px;
    margin-bottom: 16px;
    border-radius: 12px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 60vh;
    border-radius: 12px;
    background: #ffffff;
  }

  .auction-container {
    display: flex;
    margin: 0 auto;
    gap: 16px;
  }
}
</style>
